import AllBrandsSearch from "src/organisms/AllBrandsSearch";
import { GetStaticPropsContext, GetStaticPropsResult } from "next";
import getConfig from "next/config";
import { NavigationItems, SeoQueryResponse } from "src/types/graphql.d";
import { ShopType } from "src/utils/genderLocale/genderLocale";
import { getNavigationItems } from "src/services/graphql/get-navigation-items";
import { getSeoContent } from "src/services/graphql/get-seo-content";
import { getBrands } from "src/utils/search-provider/get-brands";
import { BRAND_SLUG_LOCALE_TRANSLATION } from "src/utils/constants";
import type { Locale } from "src/lib/Intl";
import type { AllBrandData } from "src/types/adapters/browse.type";
import { buildStaticProps } from "src/utils/static-props/app-page-props";

type PageProps = {
  gender: ShopType;
  locale: string;
  navigationItems?: NavigationItems | null;
  brands: AllBrandData[];
  seoContent?: SeoQueryResponse | null;
};

type Props = {
  pageProps: PageProps;
};

export default function BrandsPage({ pageProps }: Props): React.ReactElement {
  return (
    <AllBrandsSearch
      locale={pageProps.locale}
      brands={pageProps.brands}
      seoContent={pageProps.seoContent}
    />
  );
}

export async function getStaticProps({}: GetStaticPropsContext): Promise<
  GetStaticPropsResult<PageProps>
> {
  const { serverRuntimeConfig } = getConfig();
  const locale = serverRuntimeConfig?.LOCALE || process.env.LOCALE || "en";

  // Bail out during build time — return an empty, revalidatable entry so the
  // Redis cache stores a normal ISR result that background revalidation can
  // refresh once CONSTRUCTOR_INDEX_KEY is available.
  if (!process.env.CONSTRUCTOR_INDEX_KEY) {
    return buildStaticProps(
      {
        gender: ShopType.All,
        locale,
        navigationItems: null,
        brands: [],
      },
      60
    );
  }

  const [{ navigationItems }, brands, seoContent] = await Promise.all([
    getNavigationItems(),
    getBrands(locale),
    getSeoContent(`/${BRAND_SLUG_LOCALE_TRANSLATION[locale as Locale]}`),
  ]);

  return buildStaticProps(
    {
      gender: ShopType.All,
      locale,
      navigationItems,
      brands,
      seoContent,
    },
    60 * 30
  );
}
